import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import { ReactComponent as BookmarkIcon } from '../../../../assets/superAdmin-Dashboard/BookmarkIcon.svg';
import { ReactComponent as TrueIcon } from '../../../../assets/superAdmin-Dashboard/TrueIcon.svg';
import { classes, Root } from './utils';
import {
  getHelperText,
  SUBSCRIPTION_PLAN,
} from '../../../../constants/staticContent';
import {
  AddSubscriptionPlan,
  deleteSubscriptionPlan,
  discontinueSubscriptionPlan,
  getAllSubscriptionPlan,
  updateSubscriptionPlan,
} from '../../../../services/subscriptionPlanApi';
import ConfirmDialog from '../../utils/ConfirmDialog';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import ROLES from '../../../../constants/roles';
import { lightPalette } from '../../../../theme';

const SubscriptionPlan = () => {
  const [open, setOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDiscontinueDialogOpen, setIsDiscontinueDialogOpen] = useState(false);
  const [planIdToDelete, setPlanIdToDelete] = useState(null);
  const [planIdToDiscontinue, setPlanIdToDiscontinue] = useState(null);
  const [statusToDiscontinue, setStatusToDiscontinue] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    expiry: '',
    price: '',
    credits: '',
    description: '',
  });
  const [plans, setPlans] = useState([]); // Store all plans here
  const [errors, setErrors] = useState({
    name: false,
    expiry: false,
    price: false,
    credits: false,
    description: false,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const userRole = getLocalStorageItem('role');
  const isReadOnly = userRole === ROLES.SUPER_MANAGER;
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleCloseDiscontinueDialog = () => {
    setIsDiscontinueDialogOpen(false);
  };

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const response = await getAllSubscriptionPlan(); // Call your API endpoint
      setPlans(response); // Store the fetched plans
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching plans:', error);
    }
  };
  useEffect(() => {
    fetchPlans(); // Fetch plans when the component mounts
  }, []);

  const handleDelete = (planId) => {
    setPlanIdToDelete(planId);
    setIsDialogOpen(true);
  };

  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormData({
      name: '',
      expiry: '',
      price: '',
      credits: '',
      description: '',
    });
    setOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setIsEditMode(true); // Set to "edit" mode
    setFormData({
      id: plan.id,
      name: plan.name,
      expiry: plan.expiry,
      price: plan.price,
      credits: plan.credits,
      description: plan.description,
      status: plan.status,
    });
    setOpen(true);
  };

  const handleDiscontinue = (planId, status) => {
    setPlanIdToDiscontinue(planId);
    if (status === 'active') {
      setStatusToDiscontinue('inactive');
    } else {
      setStatusToDiscontinue('active');
    }
    setIsDiscontinueDialogOpen(true);
  };

  const handleClose = () => {
    setErrors({
      name: false,
      expiry: false,
      price: false,
      credits: false,
      description: false,
    });
    setOpen(false);
  };

  const validateField = (name, value, formValue) => {
    let error = false;

    if (name === 'name' && !value) {
      error = 'Name is required';
    }
    if (formValue.name !== 'Free') {
      if (name === 'price') {
        if (!value) {
          error = 'Price is required';
        } else if (Number(value) < 0) {
          error = 'Price must be greater than 0';
        }
      }
    }
    if (name === 'expiry') {
      if (!value) {
        error = 'Expiry is required';
      } else if (Number(value) <= 0) {
        error = 'Expiry must be greater than zero';
      }
    }
    if (name === 'credits') {
      if (!value) {
        error = 'Credits are required';
      } else if (Number(value) <= 0) {
        error = 'Credits must be greater than zero';
      }
    }
    if (name === 'description' && !value) {
      error = 'Description is required';
    }

    return error;
  };

  const validateForm = () => {
    const tempErrors = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field], formData);
      if (error) {
        tempErrors[field] = error;
      }
    });

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleOnChange = (event) => {
    const { name, value } = event.target;

    // Update the form data with the new input value
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value, // Use the 'name' attribute to identify the field and update its value
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, value, formData), // Only set error if validation fails
    }));
  };

  const handleConfirmAction = async () => {
    if (planIdToDelete) {
      const result = await deleteSubscriptionPlan(planIdToDelete);
      if (result) {
        fetchPlans();
        setIsDialogOpen(false); // Close the dialog after deletion
        setPlanIdToDelete(null); // Reset the plan ID
      }
    }
  };
  const handleDiscontinueConfirmAction = async () => {
    if (planIdToDiscontinue) {
      const result = await discontinueSubscriptionPlan(
        planIdToDiscontinue,
        statusToDiscontinue,
      );
      if (result) {
        fetchPlans();
        setIsDiscontinueDialogOpen(false);
        setOpen(false);
        setPlanIdToDiscontinue(null);
        setStatusToDiscontinue(null);
      }
    }
  };

  // Example of a simple validation function (you can customize this)

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (isEditMode) {
      const result = await updateSubscriptionPlan(formData);
      if (result) {
        fetchPlans(); // Refresh the plans list
      }
    } else {
      const result = await AddSubscriptionPlan(formData);
      if (result) {
        fetchPlans();
      }
    }
    setOpen(false);
  };
  const hasAnyError = Object.values(errors).some((value) => value === true);

  return (
    <Root className={classes.root}>
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Box className={classes.headingBox}>
            <Typography variant="h4" className={classes.subscriptionPlanTitle}>
              {SUBSCRIPTION_PLAN.MAIN_HEADING}
            </Typography>
            {!isReadOnly && (
              <Button
                variant="contained"
                onClick={handleOpenAdd}
                className={classes.addButton}
              >
                ADD PLAN
              </Button>
            )}
          </Box>
          <Grid
            container
            rowGap={2}
            alignContent="baseline"
            spacing={2}
            padding={2}
            className={classes.mainGridClass}
          >
            {plans
              .sort((a, b) => a.price - b.price)
              .map((plan) => (
                <Grid item xs={12} sm={12} md={6} lg={4} key={plan.id}>
                  <Card className={classes.card}>
                    <CardContent className={classes.CardContentBox}>
                      <Box>
                        <Box className={classes.subscriptionPlanCardHeader}>
                          <Box
                            className={classes.subscriptionPlanHeaderContent}
                          >
                            <Box
                              className={classes.subscriptionPlanHeaderSvgIcon}
                            >
                              <SvgIcon
                                component={BookmarkIcon}
                                htmlColor="#FFFFFF"
                                stroke="#9F77EB"
                              />
                            </Box>
                            <Typography variant="h4">{plan.name}</Typography>
                            <Chip
                              variant="outlined"
                              className={classes.planStatusChip}
                              label={
                                plan.status === 'active' ? 'Active' : 'Inactive'
                              }
                              icon={
                                <CircleIcon
                                  className={classes.planStatusChipIcon}
                                  style={{
                                    color:
                                      plan.status === 'active'
                                        ? lightPalette.colorGreen.main
                                        : lightPalette.colorRed.main,
                                  }}
                                />
                              }
                              sx={{
                                color:
                                  plan.status === 'active'
                                    ? lightPalette.colorGreen.main
                                    : lightPalette.colorRed.main,
                                borderColor:
                                  plan.status === 'active'
                                    ? lightPalette.colorGreen.main
                                    : lightPalette.colorRed.main,
                              }}
                            />
                          </Box>
                        </Box>
                        <Box className={classes.creditAndExpiryBox}>
                          <Box>
                            <Typography variant="h5" pb={1}>
                              Description:
                            </Typography>
                            {plan.description
                              ?.split('\n')
                              .filter((line) => line.trim() !== '')
                              .map((item) => (
                                <Box className={classes.trueIconBox} key={item}>
                                  <SvgIcon component={TrueIcon} />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      minWidth: '120px',
                                      wordWrap: 'break-word',
                                    }}
                                  >
                                    {item}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      </Box>
                      <Box className={classes.RightSideBoxOfCard}>
                        <Typography
                          variant="h4"
                          color="#9F77EB"
                          textAlign="end"
                        >
                          ₹{plan.price}
                        </Typography>
                        <Box>
                          <Box>
                            <Typography variant="subtitle1">
                              Credits Available:
                              {plan.credits}
                            </Typography>
                            <Typography variant="subtitle1">
                              Expiry In Days:
                              {plan.expiry}
                            </Typography>
                          </Box>
                          <Box className={classes.subscriptionPlanContent}>
                            <Box className={classes.actionButton}>
                              {!isReadOnly && (
                                <Button
                                  variant="contained"
                                  onClick={() => handleOpenEdit(plan)}
                                  className={classes.editButton}
                                >
                                  Edit
                                </Button>
                              )}
                              {!isReadOnly && (
                                <Button
                                  variant="contained"
                                  onClick={() => handleDelete(plan.id)}
                                  className={classes.editButton}
                                >
                                  Delete
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>
      )}
      <Backdrop className={classes.changePasswordBackdrop} open={open}>
        <Card
          className={
            hasAnyError ? classes.errorCardComponent : classes.CardComponent
          }
        >
          <CardHeader
            className={classes.CardHeader}
            title={
              <Grid container alignItems="center">
                <Grid item xs={10}>
                  <Typography variant="h4" className={classes.HeaderTypography}>
                    {isEditMode ? 'EDIT PLAN' : 'ADD NEW PLAN'}
                  </Typography>
                </Grid>
                <Grid item xs={2} align="right">
                  <Avatar
                    onClick={handleClose}
                    src={closeIcon}
                    className={classes.CloseAction}
                  />
                </Grid>
              </Grid>
            }
          />
          {isLoading ? (
            <Box className={classes.loaderBox}>
              <CircularProgress />
            </Box>
          ) : (
            <CardContent className={classes.CardContent}>
              <Box component="form" noValidate onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <Box className={classes.GridBox}>
                    <Grid container justifyContent="space-between">
                      <Grid item xs={5.85}>
                        <Box className={classes.GridItems}>
                          <Typography
                            variant="body1"
                            className={classes.GridItemsTypography}
                          >
                            Name
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            name="name"
                            className={classes.GridItemTextfield}
                            onChange={handleOnChange}
                            value={formData.name}
                            error={errors.name}
                            helperText={getHelperText(
                              'name',
                              'name',
                              errors,
                              formData,
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={5.85}>
                        <Box className={classes.GridItems}>
                          <Typography
                            variant="body1"
                            className={classes.GridItemsTypography}
                          >
                            Expiry In Days
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            name="expiry"
                            type="number"
                            className={classes.GridItemTextfield}
                            onChange={handleOnChange}
                            value={formData.expiry}
                            error={errors.expiry}
                            helperText={getHelperText(
                              'expiry',
                              'expiry',
                              errors,
                              formData,
                            )}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box className={classes.GridBox}>
                    <Grid container justifyContent="space-between">
                      <Grid item xs={5.85}>
                        <Box className={classes.GridItems}>
                          <Typography
                            variant="body1"
                            className={classes.GridItemsTypography}
                          >
                            Price
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            name="price"
                            type="number"
                            className={classes.GridItemTextfield}
                            onChange={handleOnChange}
                            value={formData.price}
                            error={errors.price}
                            helperText={getHelperText(
                              'price',
                              'price',
                              errors,
                              formData,
                            )}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={5.85}>
                        <Box className={classes.GridItems}>
                          <Typography
                            variant="body1"
                            className={classes.GridItemsTypography}
                          >
                            Credits
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            name="credits"
                            type="number"
                            className={classes.GridItemTextfield}
                            onChange={handleOnChange}
                            value={formData.credits}
                            error={errors.credits}
                            helperText={getHelperText(
                              'credits',
                              'credits',
                              errors,
                              formData,
                            )}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box className={classes.GridBox}>
                    <Box>
                      <Typography
                        className={classes.GridItemsTypography}
                        variant="body1"
                      >
                        Description
                      </Typography>
                      <Stack>
                        <TextField
                          fullWidth
                          size="small"
                          name="description"
                          className={classes.GridItemTextfield}
                          onChange={handleOnChange}
                          value={formData.description}
                          multiline
                          rows={3}
                          error={errors.description}
                          helperText={getHelperText(
                            'description',
                            'description',
                            errors,
                            formData,
                          )}
                        />
                      </Stack>
                    </Box>
                  </Box>
                </FormControl>
              </Box>

              <Box className={classes.Box}>
                <Button
                  className={classes.CancelButton}
                  onClick={() => {
                    if (isEditMode) {
                      handleDiscontinue(formData?.id, formData?.status);
                    } else {
                      handleClose();
                    }
                  }}
                >
                  <Typography variant="body1">
                    {!isEditMode
                      ? 'Cancel'
                      : formData?.status === 'active'
                        ? 'Discontinue'
                        : 'Continue'}
                  </Typography>
                </Button>
                <Button
                  variant="contained"
                  className={classes.UpdateButton}
                  onClick={handleSubmit}
                >
                  <Typography variant="body1">
                    {isEditMode ? 'Update' : 'Add'}
                  </Typography>
                </Button>
              </Box>
            </CardContent>
          )}
        </Card>
      </Backdrop>

      {isDialogOpen ? (
        <ConfirmDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmAction}
          askingMessage={SUBSCRIPTION_PLAN.ASKING_MESSAGE_OF_DELETE_PLAN}
        />
      ) : null}
      {isDiscontinueDialogOpen ? (
        <ConfirmDialog
          open={isDiscontinueDialogOpen}
          onClose={handleCloseDiscontinueDialog}
          onConfirm={handleDiscontinueConfirmAction}
          askingMessage={
            formData?.status === 'active'
              ? SUBSCRIPTION_PLAN.ASKING_MESSAGE_OF_DISCONTINUE_PLAN
              : SUBSCRIPTION_PLAN.ASKING_MESSAGE_OF_CONTINUE_PLAN
          }
        />
      ) : null}
    </Root>
  );
};

export default SubscriptionPlan;
